import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { changeLanguage } from '../i18n';
import api from '../services/api';
import { useAuth } from './AuthContext';
import { getBrowserTimezone } from '../utils/timezone';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [settings, setSettings] = useState({
    // Initial value is intentionally empty (not 'UTC') so consumers that read
    // `settings.timezone` before hydration fall through to their own default
    // (e.g. `useTimezoneFormat` uses the browser timezone). Otherwise every
    // first-paint timestamp renders as UTC and then swaps once hydration lands.
    timezone: '',
    language: 'en',
    accent_color: '#4F46E5',
    loading: true
  });

  // Apply accent color to CSS variables
  const applyAccentColor = React.useCallback((color) => {
    if (color && typeof color === 'string') {
      document.documentElement.style.setProperty('--accent-color', color);
      
      // Generate lighter and darker variants
      const rgb = hexToRgb(color);
      if (rgb) {
        const lighter = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)`;
        const light = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2)`;
        document.documentElement.style.setProperty('--accent-color-light', light);
        document.documentElement.style.setProperty('--accent-color-lighter', lighter);
      }
    }
  }, []);

  // Convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Fetch user settings from backend
  const fetchSettings = React.useCallback(async () => {
    try {
      // Fetch tenant-wide settings (branding/general fallback) and the
      // localization-info endpoint (personal + agent-shift + tenant-admin
      // timezone/language) in parallel, then resolve the effective
      // timezone/language using a strict precedence:
      //   Timezone: agent-shift timezone (device-mapped users) → personal
      //             profile timezone → tenant admin timezone → tenant
      //             general.timezone → 'UTC' (no arbitrary hardcoded zone).
      //   Language: personal profile language → tenant admin language →
      //             tenant general.language → 'en'.
      const [tenantResponse, locResponse] = await Promise.allSettled([
        api.get('/api/ca/settings/general'),
        api.get('/api/profile/localization-info'),
      ]);

      const general =
        tenantResponse.status === 'fulfilled'
          ? (tenantResponse.value.data?.general || {})
          : {};

      const loc =
        locResponse.status === 'fulfilled'
          ? (locResponse.value.data || {})
          : {};

      // Device-mapped (desktop-agent) users: their monitored activity is tied
      // to their team's shift clock, so that timezone overrides their own
      // profile preference for display purposes.
      const effectiveTimezone =
        (loc.is_agent_user && loc.agent_timezone) ||
        loc.user_timezone ||
        loc.admin_timezone ||
        general.timezone ||
        'UTC';

      const effectiveLanguage =
        loc.user_language ||
        loc.admin_language ||
        general.language ||
        'en';

      setSettings({
        timezone: effectiveTimezone,
        language: effectiveLanguage,
        accent_color: general.branding?.accent_color || '#4F46E5',
        loading: false,
      });

      // Apply accent color to CSS variables
      applyAccentColor(general.branding?.accent_color || '#4F46E5');

      // Apply language change (including RTL for Arabic).
      // Uses the user's personal choice when available; falls back to tenant default.
      changeLanguage(effectiveLanguage);
    } catch (error) {
      // User not authenticated or settings not available - use defaults
      if (error.response?.status !== 401) {
        console.error('Error fetching settings:', error);
      }
      setSettings(prev => ({ ...prev, loading: false }));
    }
  }, [applyAccentColor]);

  // Format date based on user's timezone
  const formatDate = (dateStr, options = {}) => {
    if (!dateStr) return 'N/A';
    
    try {
      const date = new Date(dateStr);
      
      // Fall back to browser timezone during pre-hydration paint so `Intl.DateTimeFormat`
      // doesn't throw on an empty `timeZone`. Consumers that call this on first render
      // still see a sensible local-time string instead of an error.
      const effectiveTz = settings.timezone || getBrowserTimezone();
      // Default options
      const defaultOptions = {
        timeZone: effectiveTz,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      };

      const mergedOptions = { ...defaultOptions, ...options };

      // Format the date
      const formatter = new Intl.DateTimeFormat(settings.language || 'en', mergedOptions);
      let formattedDate = formatter.format(date);

      // Add timezone abbreviation
      const tzName = getTimezoneName(effectiveTz);
      formattedDate += ` ${tzName}`;
      
      return formattedDate;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateStr;
    }
  };

  // Get timezone abbreviation
  const getTimezoneName = (timezone) => {
    const tzMap = {
      'Asia/Kolkata': 'IST',
      'America/New_York': 'EST/EDT',
      'America/Los_Angeles': 'PST/PDT',
      'Europe/London': 'GMT/BST',
      'Europe/Paris': 'CET/CEST',
      'Asia/Tokyo': 'JST',
      'Australia/Sydney': 'AEDT/AEST',
      'Asia/Dubai': 'GST'
    };
    return tzMap[timezone] || timezone;
  };

  // Update settings (called when user changes settings)
  const updateSettings = (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
    if (newSettings.accent_color) {
      applyAccentColor(newSettings.accent_color);
    }
    // Change i18n language when language setting is updated
    if (newSettings.language) {
      changeLanguage(newSettings.language);
    }
  };

  // Re-fetch settings whenever the user authenticates (login or page-refresh with
  // an existing session). This ensures that the user's saved language, timezone,
  // and accent colour are applied even when they log in without a full page reload
  // (e.g. logout → login in the same browser tab).
  useEffect(() => {
    if (isAuthenticated) {
      fetchSettings();
    }
  }, [isAuthenticated, fetchSettings]);

  const value = {
    settings,
    updateSettings,
    refreshSettings: fetchSettings,
    formatDate,
    getTimezoneName
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext;
